import { useEffect, useState } from "react";
import { api, errText } from "../lib/api";
import { Loading, vc, formatNum } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { BarChart, Bar, LineChart, Line, ResponsiveContainer, XAxis, Tooltip, CartesianGrid } from "recharts";
import { Users, Music2, Film, Eye, Heart, Coins, TrendingUp, Clapperboard, Sparkles, Send } from "lucide-react";
import { toast } from "sonner";

export default function Studio() {
  const [o, setO] = useState(null);
  const [advisor, setAdvisor] = useState([]);
  const [q, setQ] = useState("");
  const [thinking, setThinking] = useState(false);

  useEffect(() => { api.get("/studio/overview").then(({ data }) => setO(data)); }, []);

  const ask = async () => {
    if (!q.trim()) return;
    const msg = q; setQ(""); setAdvisor((a) => [...a, { role: "user", text: msg }]); setThinking(true);
    try { const { data } = await api.post("/ai/career-advisor", { message: msg }); setAdvisor((a) => [...a, { role: "ai", text: data.reply }]); }
    catch (e) { toast.error(errText(e)); setAdvisor((a) => [...a, { role: "ai", text: "The advisor is unavailable right now." }]); }
    finally { setThinking(false); }
  };

  if (!o) return <Loading />;
  const engagement = [{ d: "Mon", v: 12 }, { d: "Tue", v: 19 }, { d: "Wed", v: 15 }, { d: "Thu", v: 27 }, { d: "Fri", v: 34 }, { d: "Sat", v: 41 }, { d: "Sun", v: 38 }];
  const growth = [{ d: "W1", v: Math.max(1, o.followers - 300) }, { d: "W2", v: Math.max(1, o.followers - 180) }, { d: "W3", v: Math.max(1, o.followers - 60) }, { d: "W4", v: o.followers }];

  return (
    <div className="px-4 md:px-6 py-5">
      <div className="flex items-center gap-2 mb-5"><Clapperboard className="text-eblue" /><h1 className="font-display font-bold text-2xl">Creator Studio</h1></div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <KPI icon={Users} label="Followers" value={formatNum(o.followers)} />
        <KPI icon={TrendingUp} label="VStar Score" value={Number(o.vstar_score).toFixed(0)} accent />
        <KPI icon={Eye} label="Total Streams" value={formatNum(o.total_streams)} />
        <KPI icon={Heart} label="Likes" value={formatNum(o.likes)} />
      </div>

      <div className="grid lg:grid-cols-2 gap-4 mb-5">
        <div className="surface rounded-2xl p-5"><div className="font-display font-semibold mb-3">Weekly Engagement</div>
          <ResponsiveContainer width="100%" height={180}><BarChart data={engagement}><CartesianGrid strokeOpacity={0.05} vertical={false} /><XAxis dataKey="d" stroke="#71717a" fontSize={11} /><Tooltip contentStyle={{ background: "#0A0D18", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }} /><Bar dataKey="v" fill="#00E5FF" radius={[4, 4, 0, 0]} /></BarChart></ResponsiveContainer>
        </div>
        <div className="surface rounded-2xl p-5"><div className="font-display font-semibold mb-3">Follower Growth</div>
          <ResponsiveContainer width="100%" height={180}><LineChart data={growth}><CartesianGrid strokeOpacity={0.05} vertical={false} /><XAxis dataKey="d" stroke="#71717a" fontSize={11} /><Tooltip contentStyle={{ background: "#0A0D18", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8 }} /><Line dataKey="v" stroke="#D4AF37" strokeWidth={2} dot={false} /></LineChart></ResponsiveContainer>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="surface rounded-2xl p-5">
          <div className="font-display font-semibold mb-4">Earnings</div>
          <div className="grid grid-cols-3 gap-3">
            <Earn label="Available" value={o.wallet.available} accent /><Earn label="Pending" value={o.wallet.pending} /><Earn label="Lifetime" value={o.wallet.lifetime} />
          </div>
          <div className="grid grid-cols-3 gap-3 mt-3 text-center text-sm">
            <Mini icon={Music2} label="Tracks" value={o.tracks} /><Mini icon={Film} label="Reels" value={o.reels} /><Mini icon={Coins} label="Tips" value={o.tips} />
          </div>
        </div>

        <div className="surface rounded-2xl p-5 flex flex-col">
          <div className="font-display font-semibold mb-3 flex items-center gap-2"><Sparkles size={16} className="text-gold" /> VStar Career Advisor</div>
          <div className="flex-1 min-h-[160px] max-h-64 overflow-y-auto space-y-2 mb-3 hide-scroll">
            {advisor.length === 0 && <p className="text-xs text-muted-foreground">Ask the AI advisor how to grow your audience, climb VCharts or earn more VirtuCoins.</p>}
            {advisor.map((m, i) => <div key={i} className={`text-sm rounded-xl px-3 py-2 ${m.role === "user" ? "bg-eblue/10 ml-6" : "surface-2 mr-6"}`}>{m.text}</div>)}
            {thinking && <div className="text-sm surface-2 rounded-xl px-3 py-2 mr-6 text-muted-foreground">Thinking…</div>}
          </div>
          <div className="flex gap-2">
            <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && ask()} placeholder="How do I grow faster?" className="surface-2 border-white/10" data-testid="advisor-input" />
            <Button onClick={ask} disabled={thinking} className="btn-gold rounded-xl" data-testid="advisor-send"><Send size={16} /></Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function KPI({ icon: Icon, label, value, accent }) { return <div className={`rounded-2xl p-4 ${accent ? "glass glass-gold" : "surface"}`}><Icon size={18} className={accent ? "text-gold" : "text-muted-foreground"} /><div className={`font-display font-bold text-2xl mt-2 ${accent ? "text-gold" : ""}`}>{value}</div><div className="text-xs text-muted-foreground">{label}</div></div>; }
function Earn({ label, value, accent }) { return <div className="surface-2 rounded-xl p-3 text-center"><div className={`font-mono text-lg ${accent ? "text-gold" : ""}`}>{vc(value)}</div><div className="text-xs text-muted-foreground">{label} VC</div></div>; }
function Mini({ icon: Icon, label, value }) { return <div className="surface-2 rounded-xl p-3"><Icon size={16} className="mx-auto text-muted-foreground mb-1" /><div className="font-semibold">{value}</div><div className="text-xs text-muted-foreground">{label}</div></div>; }
